import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { m as motion, useMotionValue, useTransform } from 'framer-motion';
import * as Icons from '@polestar/component-warehouse-react/tokens/icons/Icons';
import { hasPassiveSupport, isPointingDeviceCoarse, } from '@polestar/component-warehouse-react/helpers/supports';
import useCallbackRef from '@polestar/component-warehouse-react/hooks/useCallbackRef';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import useResizeCallback from '@polestar/component-warehouse-react/hooks/useResizeCallback';
import { usePointerReplace } from '@polestar/component-warehouse-react/components/pointer-replace/PointerReplaceContext';
import useMediaQuery from '@polestar/component-warehouse-react/hooks/useMediaQuery';
import DeviceTypes from '@polestar/component-warehouse-react/tokens/device-types/DeviceTypes';
import { GalleryVariants } from '@polestar/component-warehouse-react/components/gallery/Gallery';
export var Directions;
(function (Directions) {
    Directions["left"] = "left";
    Directions["right"] = "right";
    Directions["none"] = "none";
    Directions["hidden"] = "hidden";
})(Directions || (Directions = {}));
const PointerReplace = memo(({ onClick, variant, canNavigateBackward, canNavigateForward, onMouseEnter, onMouseLeave, onMouseUp, children, ...props }) => {
    const [offset, setOffset] = useState({ left: 0, width: 0 });
    const [positionRef, positionNode] = useCallbackRef();
    const { setIcon, setEmpty, hide, opacity, iconOffsetX } = usePointerReplace();
    // Set pointer opacity
    opacity === null || opacity === void 0 ? void 0 : opacity.set(0.6);
    const handleUpdateOffset = useCallback((node = undefined) => {
        if (node || positionNode) {
            const { left, width } = (node || positionNode).getBoundingClientRect();
            setOffset({ left, width });
        }
    }, [positionNode]);
    const handleResize = useResizeCallback((node) => {
        handleUpdateOffset(node);
    }, [handleUpdateOffset]);
    const combinedRef = useCombinedRef(handleResize, positionRef);
    useEffect(() => {
        let timeout;
        const resize = () => {
            timeout && clearTimeout(timeout);
            timeout = setTimeout(() => {
                handleUpdateOffset();
            }, 200);
        };
        window.addEventListener('resize', resize, hasPassiveSupport ? { passive: true } : false);
        return () => {
            timeout && clearTimeout(timeout);
            window.removeEventListener('resize', resize);
        };
    }, [handleUpdateOffset]);
    const hasHover = useRef(false);
    const direction = useMotionValue(Directions.hidden);
    useEffect(() => {
        if (variant === GalleryVariants.standard ||
            variant === GalleryVariants.highContrastChevrons) {
            return;
        }
        return direction.on('change', (direction) => {
            if (direction === Directions.hidden) {
                hide();
            }
            else if (direction === Directions.none) {
                if (variant === GalleryVariants.contained) {
                    hide();
                }
                else {
                    setEmpty();
                }
            }
            else {
                setIcon(direction === Directions.left ? Icons.chevronLeft : Icons.chevronRight);
            }
        });
    }, [setIcon, direction, hide, setEmpty, variant]);
    // Used to calculate the width of the hoverable areas
    const { navigateRight, navigateLeft } = useMemo(() => {
        const centerPoint = offset.left + offset.width / 2;
        const navigateRight = centerPoint;
        const navigateLeft = centerPoint;
        return { navigateRight, navigateLeft };
    }, [offset.left, offset.width]);
    const handleMouseMove = useCallback((e) => {
        if (!hasHover.current || variant === GalleryVariants.highContrastChevrons) {
            return;
        }
        if (e.target === e.currentTarget) {
            direction.set(Directions.none);
        }
        else if (navigateRight > e.clientX && canNavigateBackward.get()) {
            direction.set(Directions.left);
            iconOffsetX === null || iconOffsetX === void 0 ? void 0 : iconOffsetX.set(-1);
        }
        else if (navigateLeft < e.clientX && canNavigateForward.get()) {
            direction.set(Directions.right);
            iconOffsetX === null || iconOffsetX === void 0 ? void 0 : iconOffsetX.set(1);
        }
        else {
            direction.set(Directions.hidden);
        }
    }, [
        variant,
        canNavigateBackward,
        canNavigateForward,
        direction,
        navigateRight,
        navigateLeft,
        iconOffsetX,
    ]);
    const handleMouseEnter = useCallback((e) => {
        hasHover.current = true;
        // Calculate offsets. This is needed initially or after a resize because when using a gallery in a drawer or
        // dialog, the offset is not correct until after the drawer has finished animating.
        handleUpdateOffset(e.currentTarget);
        onMouseEnter === null || onMouseEnter === void 0 ? void 0 : onMouseEnter(e);
    }, [handleUpdateOffset, onMouseEnter]);
    const handleMouseLeave = useCallback((e) => {
        onMouseLeave === null || onMouseLeave === void 0 ? void 0 : onMouseLeave(e);
        direction.set(Directions.hidden);
        hide();
    }, [onMouseLeave, direction, hide]);
    const handleClick = useCallback(() => {
        onClick === null || onClick === void 0 ? void 0 : onClick(direction.get());
    }, [direction, onClick]);
    const handleMouseUp = useCallback((e) => {
        onMouseUp === null || onMouseUp === void 0 ? void 0 : onMouseUp(e);
    }, [onMouseUp]);
    const { name } = useMediaQuery();
    const pointerEventsLeft = useTransform(canNavigateBackward, (latestCanNavigateLeft) => latestCanNavigateLeft ? 'auto' : 'none');
    const pointerEventsRight = useTransform(canNavigateForward, (latestCanNavigateRight) => latestCanNavigateRight ? 'auto' : 'none');
    return (React.createElement(Container, { ref: combinedRef, onMouseEnter: handleMouseMove, onMouseMove: handleMouseMove, onMouseUp: handleMouseUp, onTouchStart: handleMouseMove, onTouchEnd: handleMouseUp, onClick: handleClick, onHoverStart: handleMouseEnter, onMouseLeave: handleMouseLeave, variant: variant, ...props }, variant === GalleryVariants.highContrastChevrons ||
        (isPointingDeviceCoarse && name === DeviceTypes.mobile) ? (children) : (React.createElement(React.Fragment, null,
        React.createElement(ClickStopper, { left: true, style: {
                pointerEvents: pointerEventsLeft,
            } }),
        children,
        React.createElement(ClickStopper, { style: {
                pointerEvents: pointerEventsRight,
            } })))));
});
PointerReplace.displayName = 'PointerReplace';
export default PointerReplace;
const Container = styled(motion.div)(({ variant }) => css(css `
      position: relative;
      width: 100%;
      pointer-events: auto;
      flex: 1;
      & > * {
        pointer-events: auto;
      }
    `, variant === GalleryVariants.standard &&
    css `
        @media (hover: hover) {
          cursor: pointer;
          a,
          button {
            cursor: pointer;
          }
        }
      `, variant === GalleryVariants.highContrastChevrons &&
    css `
        & > * {
          pointer-events: none;
        }
        @media (hover: hover) {
          a,
          button {
            cursor: pointer;
            pointer-events: auto;
          }
        }
      `, (variant === GalleryVariants.standard || variant === GalleryVariants.highContrastChevrons) &&
    css `
        /* Fix for chrome where subpixel is rendered within 100%,
so you see 1px of the next slide */
        min-width: calc(100% + 2px);
        margin-inline-start: -1px;
      `));
const ClickStopper = styled(motion.div)(({ left }) => css(css `
      inset-block-start: 0;
      position: absolute;
      height: 100%;
      width: ${100 / 2}%;
      z-index: 2;
      opacity: 0;
    `, left &&
    css `
        inset-inline-start: 0;
      `, !left &&
    css `
        inset-inline-end: 0;
      `));
